/**
 * Image Button Component
 * Button for uploading and inserting images
 */

import React, { useState, useRef } from 'react'
import { EditorView } from '@codemirror/view'
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { getSelection } from '@/utils/latex/insertionHelpers'
import { EditorSelection } from '@codemirror/state'

interface ImageButtonProps {
  view: EditorView | null
  theme?: 'light' | 'dark'
  onImageUpload?: (file: File) => Promise<string> // Returns the path/URL of uploaded image
}

const ImageButton: React.FC<ImageButtonProps> = ({ view, theme = 'light', onImageUpload }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [imagePath, setImagePath] = useState('')
  const [width, setWidth] = useState('0.5')
  const [caption, setCaption] = useState('')
  const [label, setLabel] = useState('')
  const [wrapInFigure, setWrapInFigure] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !view) return

    setIsUploading(true)
    try {
      let path = ''
      if (onImageUpload) {
        path = await onImageUpload(file)
      } else {
        // Fallback: use filename
        path = file.name
      }
      setImagePath(path)
    } catch (error) {
      console.error('Image upload failed:', error)
      // Still allow manual path entry
    } finally {
      setIsUploading(false)
    }
  }

  const handleInsert = () => {
    if (!view || !imagePath.trim()) return

    const selection = getSelection(view)
    let insertText = ''

    if (wrapInFigure) {
      insertText = '\\begin{figure}[h]\n'
      insertText += '  \\centering\n'
      insertText += `  \\includegraphics[width=${width}\\textwidth]{${imagePath.trim()}}\n`
      if (caption) {
        insertText += `  \\caption{${caption}}\n`
      }
      if (label) {
        insertText += `  \\label{${label}}\n`
      }
      insertText += '\\end{figure}'
    } else {
      insertText = `\\includegraphics[width=${width}\\textwidth]{${imagePath.trim()}}`
    }

    const cursorPos = selection.from + insertText.length

    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: insertText
      },
      selection: {
        anchor: cursorPos
      }
    })

    view.focus()
    setIsDialogOpen(false)
    setImagePath('')
    setCaption('')
    setLabel('')
  }

  const buttonClass = clsx(
    'p-1.5 rounded transition-colors',
    theme === 'dark'
      ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
      : 'text-gray-800 hover:bg-gray-100 hover:text-gray-900',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  )

  const dialogClass = clsx(
    'fixed inset-0 z-50 flex items-center justify-center',
    theme === 'dark' ? 'bg-black/50' : 'bg-black/30'
  )

  const contentClass = clsx(
    'relative rounded-lg shadow-xl p-6 w-full max-w-md',
    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
  )

  const inputClass = clsx(
    'w-full px-3 py-2 rounded-md border text-sm',
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
  )

  return (
    <>
      <button
        type="button"
        onClick={() => setIsDialogOpen(true)}
        disabled={!view}
        className={buttonClass}
        title="Insert image - Upload and insert image with \\includegraphics{} or wrap in figure environment"
        aria-label="Insert image"
      >
        <PhotoIcon className="w-4 h-4" />
      </button>

      {isDialogOpen && (
        <div className={dialogClass} onClick={() => setIsDialogOpen(false)}>
          <div className={contentClass} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsDialogOpen(false)}
              className={clsx(
                'absolute top-4 right-4 p-1 rounded-md',
                theme === 'dark' ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
              )}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <h3 className={clsx('text-lg font-semibold mb-4', theme === 'dark' ? 'text-gray-100' : 'text-gray-900')}>
              Insert Image
            </h3>

            <div className="space-y-4">
              <div>
                <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                  Upload Image
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className={clsx(
                    'w-full px-4 py-2 rounded-md text-sm font-medium transition-colors',
                    theme === 'dark'
                      ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                    'disabled:opacity-50'
                  )}
                >
                  {isUploading ? 'Uploading...' : 'Choose File'}
                </button>
              </div>

              <div>
                <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                  Image Path
                </label>
                <input
                  type="text"
                  value={imagePath}
                  onChange={(e) => setImagePath(e.target.value)}
                  placeholder="path/to/image.png"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                  Width (as fraction of textwidth)
                </label>
                <input
                  type="text"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="0.5"
                  className={inputClass}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="wrap-figure"
                  checked={wrapInFigure}
                  onChange={(e) => setWrapInFigure(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="wrap-figure" className={clsx('text-sm', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                  Wrap in figure environment
                </label>
              </div>

              {wrapInFigure && (
                <>
                  <div>
                    <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                      Caption (optional)
                    </label>
                    <input
                      type="text"
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Figure caption"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                      Label (optional)
                    </label>
                    <input
                      type="text"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="fig:myfigure"
                      className={inputClass}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsDialogOpen(false)}
                className={clsx('px-4 py-2 rounded-md text-sm', theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100')}
              >
                Cancel
              </button>
              <button
                onClick={handleInsert}
                disabled={!imagePath.trim()}
                className="px-4 py-2 rounded-md text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                Insert Image
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ImageButton

